/*
 * Pure helpers over attention items: where an item's thread lives and opens,
 * whether my snooze on it is running, and whether it still counts.
 */
import { panelThreadPath } from "@/features/agents/routes";
import type { AgentScope } from "@/features/agents/types";
import {
  caseTabId,
  clientTabId,
  HOME_TAB_ID
} from "@/features/appTabs/tabRoutes";

import type { AttentionItem } from "./types";

export const itemScope = (
  item: Pick<AttentionItem, "scope_kind" | "scope_id">
): Pick<AgentScope, "kind" | "id"> => ({
  kind: item.scope_kind,
  id: item.scope_id
});

/** The app tab an item's thread opens in. Practice threads open on Home. */
export const tabIdForScope = (kind: AgentScope["kind"], id: string): string =>
  kind === "case"
    ? caseTabId(id)
    : kind === "client"
    ? clientTabId(id)
    : HOME_TAB_ID;

export const itemTabId = (
  item: Pick<AttentionItem, "scope_kind" | "scope_id">
): string => tabIdForScope(item.scope_kind, item.scope_id);

/** Where Review / Answer / Open goes: the thread itself. */
export const itemRoute = (
  item: Pick<AttentionItem, "scope_kind" | "scope_id" | "thread_id">
): string => panelThreadPath(itemScope(item), item.thread_id);

export const isSnoozed = (
  item: Pick<AttentionItem, "snoozed_until">,
  now = Date.now()
): boolean => {
  const t = item.snoozed_until ? Date.parse(item.snoozed_until) : NaN;
  return Number.isFinite(t) && t > now;
};

/** How long a clean finish keeps counting toward the Agents badge. */
export const FINISHED_COUNTS_MS = 24 * 60 * 60_000;

/**
 * Whether an item counts toward the Agents badge: not snoozed, and not a
 * finish older than a day. It stays in the menu either way.
 */
export const countsTowardBadge = (
  item: Pick<AttentionItem, "kind" | "since" | "snoozed_until">,
  now = Date.now()
): boolean => {
  if (isSnoozed(item, now)) return false;
  if (item.kind !== "finished") return true;
  const since = item.since ? Date.parse(item.since) : NaN;
  return !Number.isFinite(since) || now - since < FINISHED_COUNTS_MS;
};
